import { useId } from "react";

import { cn } from "@/lib/chillnn/cn";

import { Checkbox } from "@/components/ui/input/checkbox";

import { FormErrorMessage, FormItem } from "../../../_base/form";

import type { FormOptionType } from "../../../_types/type";
import type { CheckedState } from "@radix-ui/react-checkbox";

export type CheckboxGroupFieldProps = {
	value: unknown[];
	options: FormOptionType[];
	handleCheckedChange: (checked: CheckedState, item: FormOptionType) => void;
	/**
	 * @default 1
	 * チェックボックスの列数
	 */
	gridCols?: 1 | 2 | 3;
	callbackFunc?: (checked: CheckedState, item: FormOptionType) => void;

	errorMessage?: string | undefined;
};

export const CheckboxGroupField = (props: CheckboxGroupFieldProps): JSX.Element | null => {
	const { value, options, handleCheckedChange, gridCols = 1, callbackFunc, errorMessage } = props;
	const gridColsNumStyle = gridCols === 1 ? "grid-cols-1" : gridCols === 2 ? "grid-cols-2" : "grid-cols-3";
	const uniqueID = useId();

	return (
		<FormItem>
			<div className={cn("grid gap-x-2", gridColsNumStyle)}>
				{options.map((item, idx) => {
					const key = `${uniqueID}-${idx}`;
					return (
						<CheckboxGroupFieldItem
							key={key}
							item={item}
							handleCheckedChange={handleCheckedChange}
							callbackFunc={callbackFunc}
							value={value}
						/>
					);
				})}
			</div>
			<FormErrorMessage>{errorMessage}</FormErrorMessage>
		</FormItem>
	);
};

type Props = {
	item: FormOptionType;
} & Pick<CheckboxGroupFieldProps, "handleCheckedChange" | "callbackFunc" | "value">;

const CheckboxGroupFieldItem = ({ item, handleCheckedChange, callbackFunc, value }: Props) => {
	const uniqueID = useId();

	return (
		<div className="flex flex-row items-center space-x-3">
			{/* Noto Sans JP は ascent と descent が非対称で、字面の中心が行ボックスの中心より約 1px 下に来る。同じ分だけ下げて字面にそろえる */}
			<Checkbox
				className="translate-y-[1px]"
				id={uniqueID}
				disabled={item.disabled}
				checked={item.value ? value.includes(item.value) : false}
				onCheckedChange={(checked) => {
					handleCheckedChange(checked, item);

					if (callbackFunc) {
						callbackFunc(checked, item);
					}
				}}
			/>
			<label
				className={cn("break-all py-1 text-sm font-normal", item.disabled && "cursor-not-allowed opacity-50")}
				htmlFor={uniqueID}
			>
				{item.label}
			</label>
		</div>
	);
};
